import type { Controls } from '../core/device';
import { clamp } from '../core/math';
import type { Random } from '../core/Random';
import { banner, roundRect } from './draw';
import type { MiniGame, MiniInput } from './types';

const LENGTH = 100;
export const COPPER_MAX_SPEED = 34;
const MAX_SPEED = COPPER_MAX_SPEED;
const ACCEL = 55;
const DECEL = 85;
/** Pass a kink faster than this and the wire snaps. */
export const KINK_SAFE_SPEED = 13;
const TIME_LIMIT = 14;

/**
 * Stripping copper wire out of a condemned trailer: hold to run the stripper down the
 * wire, but ease off at every kink or it snaps. Jerry pays by the pound.
 */
export class CopperGame implements MiniGame {
  readonly title = "Strippin' Copper";
  readonly width = 360;
  readonly height = 220;
  /** How far down the wire you've stripped (0..100). */
  pos = 0;
  speed = 0;
  readonly kinks: number[];
  snapped = false;
  private t = 0;
  private overT = -1;
  private flash = 0;

  constructor(
    rng: Random,
    /** Pounds of copper in this wire if you strip all of it. */
    readonly maxLbs: number,
    kinks?: number[],
  ) {
    if (kinks) this.kinks = kinks;
    else {
      const n = rng.int(3, 5);
      const gap = LENGTH / (n + 1);
      this.kinks = Array.from({ length: n }, (_, i) =>
        Math.round(gap * (i + 1) + rng.range(-gap * 0.3, gap * 0.3)),
      );
    }
  }

  get over(): boolean {
    return this.overT >= 0;
  }

  get done(): boolean {
    return this.over && this.t - this.overT > 1;
  }

  /** Pounds stripped so far. */
  get lbs(): number {
    return Math.round(((this.maxLbs * this.pos) / LENGTH) * 10) / 10;
  }

  get timeLeft(): number {
    return Math.max(0, TIME_LIMIT - this.t);
  }

  hint(controls: Controls): string {
    const hold = { keys: 'Hold Space/E', touch: 'Hold anywhere', pad: 'Hold A' }[controls];
    return `${hold} to strip. Let off at the red kinks or the wire snaps.`;
  }

  update(dt: number, input: MiniInput): void {
    this.t += dt;
    this.flash = Math.max(0, this.flash - dt * 2);
    if (this.over) return;
    this.speed = input.held
      ? Math.min(MAX_SPEED, this.speed + ACCEL * dt)
      : Math.max(0, this.speed - DECEL * dt);
    const next = Math.min(LENGTH, this.pos + this.speed * dt);
    for (const k of this.kinks) {
      if (this.pos < k && next >= k && this.speed > KINK_SAFE_SPEED) {
        this.pos = k;
        this.snapped = true;
        this.flash = 1;
        this.overT = this.t;
        return;
      }
    }
    this.pos = next;
    if (this.pos >= LENGTH || this.t >= TIME_LIMIT) this.overT = this.t;
  }

  draw(g: CanvasRenderingContext2D): void {
    const W = this.width;
    const H = this.height;
    g.fillStyle = '#3a3128';
    g.fillRect(0, 0, W, H);
    // Wood paneling, because it's a trailer.
    for (let x = 0; x < W; x += 30) {
      g.fillStyle = x % 60 ? '#5a4632' : '#52402d';
      g.fillRect(x, 0, 30, H);
    }
    const x0 = 24;
    const x1 = W - 24;
    const y = 104;
    const px = (p: number): number => x0 + ((x1 - x0) * p) / LENGTH;
    // Insulation, then bare copper where you've stripped it.
    g.lineCap = 'round';
    g.lineWidth = 12;
    g.strokeStyle = '#8a8f96';
    g.beginPath();
    g.moveTo(px(this.pos), y);
    g.lineTo(x1, y);
    g.stroke();
    g.lineWidth = 6;
    g.strokeStyle = '#d98a4a';
    g.beginPath();
    g.moveTo(x0, y);
    g.lineTo(px(this.pos), y);
    g.stroke();
    // Kinks.
    for (const k of this.kinks) {
      const kx = px(k);
      g.strokeStyle = this.pos > k ? 'rgba(255,90,60,0.35)' : '#ff4d3a';
      g.lineWidth = 3;
      g.beginPath();
      g.moveTo(kx - 7, y - 14);
      g.lineTo(kx + 3, y - 4);
      g.lineTo(kx - 3, y + 4);
      g.lineTo(kx + 7, y + 14);
      g.stroke();
    }
    // The stripper tool.
    const tx = px(this.pos);
    g.fillStyle = '#d62828';
    roundRect(g, tx - 10, y - 26, 20, 52, 5);
    g.fill();
    g.fillStyle = '#222';
    g.fillRect(tx - 3, y - 8, 6, 16);

    // Speed gauge: green while it's safe to hit a kink.
    const gx = 24;
    const gy = 168;
    const gw = W - 48;
    g.fillStyle = 'rgba(0,0,0,0.5)';
    g.fillRect(gx, gy, gw, 14);
    g.fillStyle = 'rgba(80,220,80,0.5)';
    g.fillRect(gx, gy, (gw * KINK_SAFE_SPEED) / MAX_SPEED, 14);
    g.fillStyle = this.speed > KINK_SAFE_SPEED ? '#ffb02e' : '#7dff6a';
    g.fillRect(gx, gy + 3, (gw * this.speed) / MAX_SPEED, 8);
    g.fillStyle = '#fff';
    g.font = '13px Anton, Impact, sans-serif';
    g.textAlign = 'left';
    g.fillText('SPEED', gx, gy - 4);
    g.textAlign = 'right';
    g.fillText(`${this.lbs.toFixed(1)} LB · ${Math.ceil(this.timeLeft)}s`, W - 24, gy - 4);

    if (this.flash > 0) {
      g.fillStyle = `rgba(255,255,255,${this.flash * 0.6})`;
      g.fillRect(0, 0, W, H);
    }
    if (this.over) {
      const text = this.snapped ? 'SNAP!' : this.pos >= LENGTH ? 'CLEAN STRIP!' : "TIME'S UP";
      banner(g, text, W / 2, 62, this.snapped ? '#ff8a6a' : '#ffd400', 40);
    }
  }
}

/** Rough pounds of copper left in a trailer's walls (it varies, nobody inventoried it). */
export const copperIn = (rng: Random): number => clamp(Math.round(rng.range(3, 6.5) * 10) / 10, 3, 6.5);
